/* One step of a chapter.
 *
 * A step is always the same three things: where you are and what kind of step
 * this is, what it says, and where it came from. The middle part is the only
 * thing that varies, and each kind of step owns its own component.
 */
import { useTranslation } from 'react-i18next';
import { blockRead } from '../../engine/sources';
import { Source } from '../Source';
import { CheckBlock } from './CheckBlock';
import { FilmBlock } from './FilmBlock';
import { GameBlock } from './GameBlock';
import { MathBlock } from './MathBlock';
import { OptionsBlock } from './OptionsBlock';
import { Prose } from './Prose';
import { TreeBlock } from './TreeBlock';
import type { Block, NodeDef } from '../../types';

interface BlockViewProps {
  node: NodeDef;
  block: Block;
  index: number;
}

export function BlockView({ node, block, index }: BlockViewProps) {
  const { t } = useTranslation();
  const read = blockRead(block);

  return (
    <>
      <div className="stack" data-od-id="block-body">
        <p className="eyebrow">
          {t('blockOf')} {('0' + (index + 1)).slice(-2)} · {t(`kind.${block.kind}`)}
        </p>
        <h2 className="h2">{t(block.titleKey)}</h2>
      </div>

      {block.kind === 'explain' && <Prose block={block} />}
      {block.kind === 'game' && (
        <>
          <Prose block={block} />
          <GameBlock block={block} />
        </>
      )}
      {block.kind === 'options' && <OptionsBlock block={block} />}
      {block.kind === 'film' && <FilmBlock block={block} />}
      {block.kind === 'math' && <MathBlock block={block} />}
      {block.kind === 'tree' && <TreeBlock block={block} />}
      {block.kind === 'check' && <CheckBlock node={node} block={block} />}

      {read.length > 0 && (
        <div className="further">
          <p className="eyebrow">{t('furtherTitle')}</p>
          {read.map((refKey) => (
            <div key={refKey} className="srcline">
              <Source refKey={refKey} bare />
            </div>
          ))}
        </div>
      )}
    </>
  );
}
